import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"

import { fetchScheduleEvent } from "../../../../../../../../../api/kehrblatt/schedule-events"
import { fetchWorkOrderDetailProjection } from "../../../../../../../../../api/kehrblatt/work-orders"
import type {
  CalendarBoardPropertyRef,
  CalendarBoardWorkOrderRef,
} from "../../../../../../../../../api/kehrblatt/schedule-events"
import ScheduleEventReadOverlay from "../../../../../_shared/_modules/schedule-event-read-overlay"
import type { LoadScheduleEventReadOverlayDetails } from "../../../../../_shared/_logic/read-contract"
import type { EmployeeOption } from "../../../../../../../_shared/_logic/calendar-types"
import type { WorkingHoursByUser } from "../../../../../../../../../_shared/_logic/form-state"

export type CalendarReadCommandContext = {
  organizationId: string | null
  canManageScheduling: boolean
  employees: EmployeeOption[]
  workOrders: CalendarBoardWorkOrderRef[]
  properties: CalendarBoardPropertyRef[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  scheduleEvents: ScheduleEvent[]
  workingHoursByUser?: WorkingHoursByUser
  forceReadonlyEventIds: Set<string>
  refreshEvents: () => Promise<void>
  upsertEvent: (event: ScheduleEvent) => void
  removeEvent: (eventId: string) => void
}

const asOverlayWorkOrders = (workOrders: CalendarBoardWorkOrderRef[]): WorkOrder[] =>
  workOrders as unknown as WorkOrder[]

const asOverlayProperties = (properties: CalendarBoardPropertyRef[]): Property[] =>
  properties as unknown as Property[]

const dedupeEvents = (events: ScheduleEvent[]) => {
  const byId = new Map<string, ScheduleEvent>()
  for (const event of events) {
    if (event?.id) byId.set(event.id, event)
  }

  return [...byId.values()]
}

const createReadOverlayDetailLoader =
  (
    context: CalendarReadCommandContext,
    event: ScheduleEvent,
  ): LoadScheduleEventReadOverlayDetails =>
  async () => {
    const detailEvent = await fetchScheduleEvent(event.id)
    const workOrderId = detailEvent.work_order_id ?? event.work_order_id ?? null
    const projection = workOrderId ? await fetchWorkOrderDetailProjection(workOrderId) : null

    return {
      event: detailEvent,
      workOrders: projection?.work_order ? [projection.work_order] : undefined,
      properties: projection?.properties,
      customers: projection?.customers,
      contactPoints: projection?.contact_points,
      contactOrders: projection?.contact_orders,
      scheduleEvents: projection
        ? dedupeEvents([
            detailEvent,
            ...(projection.schedule_events ?? []),
            ...context.scheduleEvents,
          ])
        : [detailEvent],
    }
  }

export const openCalendarEventReadOverlay = (
  context: CalendarReadCommandContext,
  event: ScheduleEvent,
) => {
  const organizationId = context.organizationId

  if (!organizationId) return

  const hasEditPermission = Boolean(context.canManageScheduling && event.can_manage)
  const forceReadOnly = !hasEditPermission || context.forceReadonlyEventIds.has(event.id)

  openOverlay({
    title: "Termin",
    layout: "record",
    content: ScheduleEventReadOverlay,
    closeOnParentOpen: true,
    isDark: true,
    properties: {
      organizationId,
      event,
      employees: context.employees,
      workOrders: asOverlayWorkOrders(context.workOrders),
      properties: asOverlayProperties(context.properties),
      customers: context.customers,
      contactPoints: context.contactPoints,
      scheduleEvents: context.scheduleEvents,
      workingHoursByUser: context.workingHoursByUser,
      forceReadOnly,
      loadDetailContext: createReadOverlayDetailLoader(context, event),
      onRefresh: context.refreshEvents,
      onSaved: async (saved: ScheduleEvent) => {
        context.upsertEvent(saved)
        await context.refreshEvents()
      },
      onDeleted: context.removeEvent,
    },
  })
}
